export interface ServiceData {
  slug: string;
  /** English title used for nav, headings and SEO. */
  title: string;
  /** Spanish title — shown alongside the English name on cards. */
  titleEs: string;
  subtitle: string;
  price: string;
  image: string;
  description: string;
  features: string[];
  process: { step: string; desc: string }[];
}

export const services: ServiceData[] = [
  {
    slug: "interior-painting",
    title: "Interior Painting",
    titleEs: "Pintura Interior",
    subtitle: "Residential & commercial interiors",
    price: "Free Estimate",
    image: "/assets/images/service-interior-painting.webp",
    description:
      "Clean lines, even coverage, and a crew that protects your home while it works. Diaz Brothers Construction paints living rooms, bedrooms, basements, stairwells, trim, doors, cabinets, offices, and commercial interiors across the Denver metro. We prep properly — patch, sand, caulk, and mask — because that is what separates a paint job that lasts from one that peels in two winters.",
    features: [
      "Walls, ceilings, trim, doors & baseboards",
      "Cabinet and built-in refinishing",
      "Drywall patching & surface prep included",
      "Low-VOC and premium washable finishes",
      "Furniture and flooring fully protected",
      "Commercial interiors, offices & rentals",
    ],
    process: [
      { step: "Walkthrough", desc: "We measure each room, review colors and sheens, and confirm the scope in writing." },
      { step: "Prep & Protect", desc: "Floors and furniture are covered, holes patched, edges caulked and sanded smooth." },
      { step: "Paint", desc: "Cut-in by hand, rolled or sprayed for even coverage — two coats standard." },
      { step: "Final Walk", desc: "We walk every room with you, touch up anything flagged, and haul off all debris." },
    ],
  },
  {
    slug: "exterior-painting",
    title: "Exterior Painting",
    titleEs: "Pintura Exterior",
    subtitle: "Built for Colorado sun, wind & snow",
    price: "Free Estimate",
    image: "/assets/images/service-exterior-painting.webp",
    description:
      "Colorado weather is brutal on exteriors — intense UV at altitude, hail, freeze-thaw cycles, and dry wind. We prep siding, stucco, trim, soffits, and fascia the right way, then apply coatings rated for this climate. The result is an exterior that still looks sharp after several seasons, not one that chalks and cracks by the next spring.",
    features: [
      "Siding, stucco, brick, trim, soffit & fascia",
      "Pressure washing & full surface prep",
      "Rotted trim and siding repair before paint",
      "Caulking and sealing of every joint & seam",
      "UV-resistant, high-altitude rated coatings",
      "HOA color-compliance guidance",
    ],
    process: [
      { step: "Inspection", desc: "We check siding, trim, caulk lines, and problem areas before quoting a dollar." },
      { step: "Wash & Repair", desc: "Surfaces are pressure washed, damaged wood replaced, and all seams re-caulked." },
      { step: "Prime & Coat", desc: "Bare and repaired areas are primed, then finished with two coats of exterior paint." },
      { step: "Cleanup", desc: "Landscaping is uncovered, the site is cleared, and we review the finished work with you." },
    ],
  },
  {
    slug: "waterproofing",
    title: "Facade Waterproofing",
    titleEs: "Impermeabilización de Fachadas",
    subtitle: "Stop water before it costs you a wall",
    price: "Free Estimate",
    image: "/assets/images/service-waterproofing.webp",
    description:
      "Water is the single most expensive thing that can get into a building. We apply elastomeric and penetrating waterproof coatings to facades, stucco, masonry, foundations, balconies, and parapet walls — sealing hairline cracks and porous surfaces so moisture stays outside where it belongs. Ideal for older stucco homes, commercial buildings, and any wall that has started to stain or spall.",
    features: [
      "Elastomeric & penetrating sealer application",
      "Stucco, masonry, block & concrete facades",
      "Crack sealing and joint repair",
      "Balcony, parapet & retaining wall coatings",
      "Efflorescence and moisture-stain treatment",
      "Residential and commercial buildings",
    ],
    process: [
      { step: "Moisture Check", desc: "We locate where water is entering — cracks, failed caulk, porous stucco, or flashing." },
      { step: "Surface Repair", desc: "Cracks are cut, filled, and patched so the coating has sound substrate to bond to." },
      { step: "Seal & Coat", desc: "A waterproof membrane or penetrating sealer is applied at manufacturer spec thickness." },
      { step: "Verify", desc: "We re-inspect coverage at every seam, edge, and penetration before calling it done." },
    ],
  },
  {
    slug: "remodeling",
    title: "Remodeling",
    titleEs: "Remodelaciones",
    subtitle: "Kitchens, baths, basements & full renovations",
    price: "Free Estimate",
    image: "/assets/images/service-remodeling.webp",
    description:
      "Complete and partial remodels handled by one crew, start to finish. Kitchens, bathrooms, basements, and whole-home refreshes — demolition, framing, drywall, tile, trim, and finish painting all under one point of contact. You get a clear scope, a realistic schedule, and a team that shows up until the punch list is empty.",
    features: [
      "Kitchen & bathroom remodels",
      "Basement finishing & conversions",
      "Demolition, framing & drywall",
      "Tile, flooring, trim & finish carpentry",
      "Full-project painting included",
      "One point of contact from start to finish",
    ],
    process: [
      { step: "Scope & Budget", desc: "We walk the space, talk through what you want, and build a written scope and budget." },
      { step: "Schedule", desc: "You get a phased timeline so you know what happens which week and who is on site." },
      { step: "Build", desc: "Demo, rough work, drywall, tile, trim, and paint — sequenced by one crew, not five." },
      { step: "Punch List", desc: "We walk the finished space with you and close out every last item before final payment." },
    ],
  },
  {
    slug: "drywall",
    title: "Drywall Installation",
    titleEs: "Drywall y Tablaroca",
    subtitle: "Filling, new texture, primer",
    price: "Free Estimate",
    image: "/assets/images/service-drywall.webp",
    description:
      "From a single ceiling patch to hanging an entire basement, we do drywall that disappears. Hang, tape, mud, sand, and texture — including matching existing knockdown, orange peel, and hand-troweled textures so repairs are invisible once painted. Water damage, cracked corners, popped nails, and popcorn-ceiling removal all handled.",
    features: [
      "New drywall hanging & finishing",
      "Tape, mud, sand & Level 5 finishes",
      "Texture matching — knockdown & orange peel",
      "Water damage & ceiling repair",
      "Popcorn ceiling removal",
      "Corner bead, crack & nail-pop repair",
    ],
    process: [
      { step: "Assess", desc: "We identify the cause — moisture, settling, or impact — so the repair actually holds." },
      { step: "Hang & Tape", desc: "Board is hung square and fastened correctly, then taped and coated in multiple passes." },
      { step: "Texture Match", desc: "We sample and match your existing texture so the patch blends into the wall." },
      { step: "Prime & Paint", desc: "Repairs are primed and painted so you never see where the work was done." },
    ],
  },
  {
    slug: "tile-installation",
    title: "Tile & Ceramic Installation",
    titleEs: "Instalación de Azulejos y Cerámica",
    subtitle: "Floors, showers, backsplashes & patios",
    price: "Free Estimate",
    image: "/assets/images/service-tile-installation.webp",
    description:
      "Tile shows every shortcut, so we don't take any. Proper substrate prep, waterproofing behind wet walls, laser-set layout, and consistent grout lines on floors, showers, tub surrounds, backsplashes, entryways, and patios. Ceramic, porcelain, and natural stone — installed to last decades, not until the first leak.",
    features: [
      "Floor, shower & tub-surround tile",
      "Kitchen & bathroom backsplashes",
      "Ceramic, porcelain & natural stone",
      "Waterproof membrane behind wet walls",
      "Substrate leveling & backer board",
      "Grout, sealing & silicone finishing",
    ],
    process: [
      { step: "Substrate Prep", desc: "Floors are leveled and wet walls get backer board plus a waterproof membrane." },
      { step: "Layout", desc: "We dry-lay and center the pattern so cuts land where they look intentional." },
      { step: "Set Tile", desc: "Tile is back-buttered and set with leveling clips for a flat, lippage-free finish." },
      { step: "Grout & Seal", desc: "Grout is floated, cleaned, and cured, then joints are siliconed and stone is sealed." },
    ],
  },
  {
    slug: "residential-services",
    title: "Comprehensive Residential Services",
    titleEs: "Servicios Residenciales Integrales",
    subtitle: "One crew for the whole punch list",
    price: "Free Estimate",
    image: "/assets/images/service-home-improvement.webp",
    description:
      "Most homeowners don't have one problem — they have a list. Diaz Brothers Construction handles the whole thing: paint, drywall, tile, trim, doors, deck and fence finishing, and the small repairs that never quite get done. One crew, one estimate, one phone number, instead of chasing five different contractors.",
    features: [
      "Full-home interior & exterior refresh",
      "Trim, doors & finish carpentry",
      "Deck, fence & railing staining",
      "Garage, basement & rental turnovers",
      "Pre-sale and move-in ready packages",
      "Small repairs bundled into one visit",
    ],
    process: [
      { step: "Your List", desc: "Send us the whole punch list — we price it as one project instead of five service calls." },
      { step: "Plan", desc: "We sequence the work so trades don't undo each other and your home stays livable." },
      { step: "Execute", desc: "One crew works the list top to bottom, keeping the site clean between phases." },
      { step: "Sign Off", desc: "Nothing is called finished until you have walked it and signed off item by item." },
    ],
  },
];
